import type { PropsWithChildren } from 'react';

import type { InlineMessageHeadingLevel } from '@ingka/inline-message';
import Text from '@ingka/text';
import type { TextStyles } from '@ingka/typography-shared';

const isTextStyle = (textStyle: string): textStyle is TextStyles => {
    return textStyle.startsWith('Heading.') || textStyle.startsWith('Body.');
};

interface TextProps {
    headingSize?: 'xs' | 's' | 'm' | 'l' | 'xl';
    bodySize?: 's' | 'm' | 'l' | 'xl';
    className?: string;
}

interface HeadingProps extends TextProps {
    headingLevel?: InlineMessageHeadingLevel;
}

/**
 * @param {'xs' | 's' | 'm' | 'l' | 'xl'} headingSize - `string` - Changes the fontSize
 * @param {string} className - `string` - Fowards the className to the text component
 * @param {'1' | '2' | '3' | '4' | '5' | '6'} headingLevel - `string` - Specifies the HTML tag to use for the heading (default is '2')
 * @returns The text wrapped and rendered using Skapa `<Text />` component
 */
export const Heading = ({
    children,
    headingSize = 's',
    className = '',
    headingLevel = '2',
}: PropsWithChildren<HeadingProps>) => {
    const textStyle = `Heading.${headingSize}`;
    const tagName = `h${headingLevel}`;
    return (
        <Text tagName={tagName} textStyle={isTextStyle(textStyle) ? textStyle : 'Heading.S'} className={className}>
            {children}
        </Text>
    );
};

/**
 * @param {'s' | 'm' | 'l' | 'xl'} bodySize - `string` - Changes the fontSize
 * @param {string} className - `string` - Fowards the className to the text component
 * @returns The text wrapped and rendered using Skapa `<Text />` component
 */
export const Paragraph = ({ children, bodySize = 'm', className = '' }: PropsWithChildren<TextProps>) => {
    const textStyle = `Body.${bodySize}`;
    return (
        <Text tagName="p" textStyle={isTextStyle(textStyle) ? textStyle : 'Body.M'} className={className}>
            {children}
        </Text>
    );
};
